import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

type Tab = 'find-id' | 'find-password';

export default function FindAccount() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('find-id');

  return (
    <div className="min-h-screen bg-background-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <button
          onClick={() => navigate('/login')}
          className="flex items-center gap-2 text-foreground-600 hover:text-foreground-900 text-sm mb-6 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          로그인으로 돌아가기
        </button>

        <div className="bg-white rounded-2xl border border-background-200 p-6 md:p-8">
          <div className="text-center mb-6">
            <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-700 mb-4">
              <i className="ri-key-2-line text-2xl"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-950">
              아이디/비밀번호 찾기
            </h1>
            <p className="text-foreground-500 text-sm mt-2">
              가입 시 입력한 정보로 계정을 찾을 수 있어요
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2 bg-background-100 p-1 rounded-full mb-6">
            <button
              type="button"
              onClick={() => setTab('find-id')}
              className={`whitespace-nowrap py-2 rounded-full text-sm font-semibold transition-colors cursor-pointer ${
                tab === 'find-id'
                  ? 'bg-white text-foreground-950 border border-background-300'
                  : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              아이디 찾기
            </button>
            <button
              type="button"
              onClick={() => setTab('find-password')}
              className={`whitespace-nowrap py-2 rounded-full text-sm font-semibold transition-colors cursor-pointer ${
                tab === 'find-password'
                  ? 'bg-white text-foreground-950 border border-background-300'
                  : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              비밀번호 찾기
            </button>
          </div>

          {tab === 'find-id' ? (
            <FindIdForm onGoToPassword={() => setTab('find-password')} />
          ) : (
            <FindPasswordForm />
          )}
        </div>
      </div>
    </div>
  );
}

function FindIdForm({ onGoToPassword }: { onGoToPassword: () => void }) {
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [foundEmails, setFoundEmails] = useState<string[]>([]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setFoundEmails([]);

    if (!fullName.trim() || !phone.trim()) {
      setError('이름과 휴대폰 번호를 모두 입력해주세요.');
      return;
    }

    setLoading(true);
    const { data, error: err } = await supabase.functions.invoke('find-account-id', {
      body: { full_name: fullName.trim(), phone: phone.trim() },
    });
    setLoading(false);

    if (err) {
      setError('아이디를 찾는 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
      return;
    }
    if (!data?.success) {
      setError(data?.message || '일치하는 계정을 찾을 수 없습니다.');
      return;
    }
    setFoundEmails(data.emails || []);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Field label="이름">
        <input
          type="text"
          name="full_name"
          required
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
          placeholder="가입 시 입력한 이름"
        />
      </Field>
      <Field label="휴대폰 번호">
        <input
          type="tel"
          name="phone"
          required
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
          placeholder="010-1234-5678"
        />
      </Field>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      {foundEmails.length > 0 && (
        <div className="bg-secondary-50 border border-secondary-200 rounded-md px-4 py-3">
          <p className="text-sm text-foreground-700 mb-2">가입된 이메일</p>
          {foundEmails.map((email) => (
            <div key={email} className="flex items-center gap-2 text-foreground-950 font-semibold text-sm">
              <i className="ri-mail-line text-secondary-600"></i>
              {email}
            </div>
          ))}
          <button
            type="button"
            onClick={onGoToPassword}
            className="mt-3 text-sm text-primary-600 hover:text-primary-700 font-semibold cursor-pointer"
          >
            비밀번호를 잊으셨나요? 비밀번호 재설정하기
          </button>
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        className="w-full whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white font-semibold py-3 rounded-md transition-colors cursor-pointer disabled:opacity-60 text-sm"
      >
        {loading ? '찾는 중...' : '아이디 찾기'}
      </button>
    </form>
  );
}

function FindPasswordForm() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('이메일을 입력해주세요.');
      return;
    }

    setLoading(true);
    const redirectTo = `${window.location.origin}${__BASE_PATH__}/reset-password`;
    const { error: err } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo,
    });
    setLoading(false);

    if (err) {
      setError('비밀번호 재설정 요청 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
      return;
    }
    setSent(true);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Field label="이메일">
        <input
          type="email"
          name="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
          placeholder="name@example.com"
        />
      </Field>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      {sent && (
        <div className="text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-4 py-3 leading-relaxed">
          입력하신 이메일로 비밀번호 재설정 링크를 보냈어요. 메일함을 확인해주세요.
          링크는 일정 시간이 지나면 만료됩니다.
        </div>
      )}

      <button
        type="submit"
        disabled={loading || sent}
        className="w-full whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white font-semibold py-3 rounded-md transition-colors cursor-pointer disabled:opacity-60 text-sm"
      >
        {loading ? '보내는 중...' : '재설정 링크 보내기'}
      </button>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-foreground-700 mb-1.5">{label}</span>
      {children}
    </label>
  );
}